import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutGrid, Users, Zap, Columns, TrendingUp } from "lucide-react";

// ─── Shared data ─────────────────────────────────────────────────────────────

const INPUT_CARDS = [
  { label: "Product", icon: LayoutGrid, color: "bg-blue-500/10 text-blue-300 border-blue-500/20" },
  { label: "Target Persona", icon: Users, color: "bg-cyan-500/10 text-cyan-300 border-cyan-500/20" },
  { label: "Narrative Opportunity", icon: Zap, color: "bg-indigo-500/10 text-indigo-300 border-indigo-500/20" },
  { label: "Narrative Angles", icon: Columns, color: "bg-sky-500/10 text-sky-300 border-sky-500/20" },
  { label: "Funnel Strategy", icon: TrendingUp, color: "bg-violet-500/10 text-violet-300 border-violet-500/20" },
];

const PSYCH_NODES = [
  { label: "motivations", angle: -60, dist: 90 },
  { label: "frustrations", angle: -10, dist: 95 },
  { label: "beliefs", angle: 50, dist: 88 },
  { label: "identity signals", angle: 120, dist: 92 },
  { label: "buying triggers", angle: 180, dist: 86 },
  { label: "objections", angle: 230, dist: 90 },
];

const ANGLE_CARDS = [
  "Founder Origin Story",
  "Social Proof",
  "Problem / Agitate / Solve",
  "Ingredient Transparency",
  "Family Safety",
  "Ancestral Wisdom",
];

const ARCHETYPES = [
  { label: "Data-Driven\nStrategist", icon: "📊" },
  { label: "Emotional\nStoryteller", icon: "🎭" },
  { label: "Contrarian\nChallenger", icon: "⚡" },
  { label: "Authority\nBuilder", icon: "🏛️" },
];

const PROFILE_LINES = [
  { label: "Name", width: "w-32" },
  { label: "Archetype", width: "w-24" },
  { label: "Philosophy", width: "w-full" },
  { label: "Strength", width: "w-3/4" },
];

// ─── Shared header ─────────────────────────────────────────────────────────────

function StepHeader({ step, title, subtitle }: { step: number; title: string; subtitle: string }) {
  return (
    <motion.div
      key={title}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="text-center space-y-1.5 mb-8"
    >
      <div className="flex items-center justify-center gap-2 mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-cyan-300">
          Step {step} of 5
        </span>
        <span className="w-1 h-1 rounded-full bg-cyan-400/40" />
        <div className="h-px bg-cyan-500/20 w-16" />
      </div>
      <h3 className="text-lg font-bold text-white tracking-tight">{title}</h3>
      <p className="text-sm text-cyan-200/70">{subtitle}</p>
    </motion.div>
  );
}

// ─── Step 1 — Campaign Inputs ────────────────────────────────────────────────

function Step1() {
  const [visible, setVisible] = useState<number[]>([]);
  const [glowing, setGlowing] = useState<number[]>([]);

  useEffect(() => {
    INPUT_CARDS.forEach((_, i) => {
      const delay = 500 * i + 150;
      setTimeout(() => setVisible((p) => [...p, i]), delay);
      setTimeout(() => setGlowing((p) => [...p, i]), delay + 700);
    });
  }, []);

  return (
    <div className="space-y-2.5">
      {INPUT_CARDS.map((card, i) => {
        const Icon = card.icon;
        const isVisible = visible.includes(i);
        const isGlowing = glowing.includes(i);
        return (
          <AnimatePresence key={card.label}>
            {isVisible && (
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 transition-all duration-700 ${card.color} ${
                  isGlowing ? "shadow-[0_0_12px_rgba(99,179,237,0.15)]" : ""
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium text-white/90">{card.label}</span>
                <motion.div
                  className="ml-auto h-1 rounded-full bg-current opacity-30"
                  initial={{ width: 0 }}
                  animate={{ width: "55%" }}
                  transition={{ delay: 0.25, duration: 1.4, ease: "easeOut" }}
                />
                {isGlowing && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className="w-2 h-2 rounded-full bg-current shrink-0"
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        );
      })}
    </div>
  );
}

// ─── Step 2 — Audience Psychology ────────────────────────────────────────────

function Step2() {
  const [revealed, setRevealed] = useState<number[]>([]);

  useEffect(() => {
    PSYCH_NODES.forEach((_, i) => {
      setTimeout(() => setRevealed((p) => [...p, i]), 350 * i + 200);
    });
  }, []);

  const cx = 110;
  const cy = 110;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative" style={{ width: 220, height: 220 }}>
        <svg className="absolute inset-0" width={220} height={220}>
          {PSYCH_NODES.map((node, i) => {
            if (!revealed.includes(i)) return null;
            const rad = (node.angle * Math.PI) / 180;
            const nx = cx + Math.cos(rad) * node.dist;
            const ny = cy + Math.sin(rad) * node.dist;
            return (
              <motion.line
                key={i}
                x1={cx} y1={cy} x2={nx} y2={ny}
                stroke="rgba(34,211,238,0.35)"
                strokeWidth={1}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              />
            );
          })}
        </svg>

        {/* Persona core */}
        <div
          className="absolute flex items-center justify-center rounded-full border border-cyan-400/40 bg-cyan-500/10"
          style={{ left: cx - 28, top: cy - 28, width: 56, height: 56 }}
        >
          <Users className="w-6 h-6 text-cyan-300" />
          <motion.div
            className="absolute inset-0 rounded-full border border-cyan-400/30"
            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>

        {/* Nodes */}
        {PSYCH_NODES.map((node, i) => {
          const rad = (node.angle * Math.PI) / 180;
          const nx = cx + Math.cos(rad) * node.dist;
          const ny = cy + Math.sin(rad) * node.dist;
          const isLeft = nx < cx;
          return revealed.includes(i) ? (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              className="absolute flex items-center"
              style={{ left: nx - 4, top: ny - 8 }}
            >
              <div className="w-2 h-2 rounded-full bg-cyan-400 shrink-0" />
              <span
                className="text-[9px] text-cyan-200/80 font-medium whitespace-nowrap"
                style={{ marginLeft: isLeft ? -60 : 6 }}
              >
                {node.label}
              </span>
            </motion.div>
          ) : null;
        })}
      </div>
    </div>
  );
}

// ─── Step 3 — Narrative Alignment ────────────────────────────────────────────

function Step3() {
  const [active, setActive] = useState<number[]>([]);
  const [pulsing, setPulsing] = useState(0);

  useEffect(() => {
    ANGLE_CARDS.forEach((_, i) => {
      setTimeout(() => setActive((p) => [...p, i]), 400 * i + 150);
    });
    const interval = setInterval(() => {
      setPulsing((p) => (p + 1) % ANGLE_CARDS.length);
    }, 600);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 justify-center mb-4">
        {/* Product & Persona anchors */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-400/25 text-xs text-blue-200 font-medium">
          <LayoutGrid className="w-3 h-3" /> Product
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/15 border border-cyan-400/25 text-xs text-cyan-200 font-medium">
          <Users className="w-3 h-3" /> Persona
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {ANGLE_CARDS.map((angle, i) => (
          <AnimatePresence key={angle}>
            {active.includes(i) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                className={`px-3 py-2 rounded-xl border text-xs font-medium text-white/80 transition-all duration-500 ${
                  pulsing === i
                    ? "bg-cyan-500/20 border-cyan-400/40 text-white shadow-[0_0_10px_rgba(34,211,238,0.2)]"
                    : "bg-white/5 border-white/10"
                }`}
              >
                {angle}
                {pulsing === i && (
                  <motion.div
                    className="mt-1.5 h-px bg-gradient-to-r from-cyan-400 to-transparent"
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        ))}
      </div>
    </div>
  );
}

// ─── Step 4 — Strategist Synthesis ────────────────────────────────────────────

function Step4() {
  const [phase, setPhase] = useState<"cycling" | "locking">("cycling");
  const [highlighted, setHighlighted] = useState(0);
  const [locked, setLocked] = useState<number | null>(null);

  useEffect(() => {
    let idx = 0;
    const cycle = setInterval(() => {
      idx = (idx + 1) % ARCHETYPES.length;
      setHighlighted(idx);
    }, 450);
    const lockTimer = setTimeout(() => {
      clearInterval(cycle);
      setPhase("locking");
      setLocked(2);
    }, 2800);
    return () => { clearInterval(cycle); clearTimeout(lockTimer); };
  }, []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {ARCHETYPES.map((arch, i) => {
          const isLocked = locked === i;
          const isActive = phase === "cycling" && highlighted === i;
          return (
            <motion.div
              key={arch.label}
              animate={{
                scale: isLocked ? 1.04 : isActive ? 1.02 : 1,
                opacity: locked !== null && !isLocked ? 0.35 : 1,
              }}
              transition={{ duration: 0.35 }}
              className={`flex flex-col items-center gap-2 rounded-xl border px-3 py-4 transition-all duration-500 ${
                isLocked
                  ? "bg-cyan-500/20 border-cyan-400/50 shadow-[0_0_16px_rgba(34,211,238,0.25)]"
                  : isActive
                  ? "bg-white/10 border-white/25"
                  : "bg-white/4 border-white/8"
              }`}
            >
              <span className="text-2xl">{arch.icon}</span>
              <span className="text-[10px] font-semibold text-center text-white/70 whitespace-pre-line leading-tight">
                {arch.label}
              </span>
              {isLocked && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-[9px] font-bold uppercase tracking-widest text-cyan-300"
                >
                  Selected
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
      {locked !== null && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center text-xs text-cyan-200/70"
        >
          Optimal creative mindset identified
        </motion.div>
      )}
    </div>
  );
}

// ─── Step 5 — Profile Forming (Loop) ─────────────────────────────────────────

function Step5() {
  const [revealed, setRevealed] = useState<number[]>([]);
  const [scanPos, setScanPos] = useState(0);

  useEffect(() => {
    PROFILE_LINES.forEach((_, i) => {
      setTimeout(() => setRevealed((p) => [...p, i]), 500 * i + 300);
    });
    const scan = setInterval(() => {
      setScanPos((p) => (p + 1) % (PROFILE_LINES.length + 2));
    }, 800);
    return () => clearInterval(scan);
  }, []);

  return (
    <div className="flex flex-col items-center gap-5">
      {/* Profile card */}
      <div className="relative w-full max-w-xs rounded-2xl border border-cyan-400/30 bg-white/5 overflow-hidden px-5 py-5">
        {/* Scan line */}
        <motion.div
          className="absolute inset-x-0 h-10 pointer-events-none"
          style={{
            background: "linear-gradient(180deg, transparent, rgba(34,211,238,0.07), transparent)",
          }}
          animate={{ top: [`${scanPos * 14}%`, `${(scanPos + 1) * 14}%`] }}
          transition={{ duration: 0.8, ease: "linear" }}
        />

        <div className="space-y-3 relative">
          {PROFILE_LINES.map((line, i) => (
            <div key={line.label} className="space-y-1">
              <div className="text-[9px] font-semibold uppercase tracking-widest text-cyan-300/60">
                {line.label}
              </div>
              {revealed.includes(i) ? (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                  className={`h-2.5 rounded-full bg-gradient-to-r from-cyan-400/40 to-blue-400/20 ${line.width}`}
                />
              ) : (
                <div className={`h-2.5 rounded-full bg-white/8 ${line.width}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Pulsing dots */}
      <div className="flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-cyan-400"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.3 }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

const STEPS = [
  { step: 1, title: "Analyzing Campaign Inputs", subtitle: "Understanding the campaign environment" },
  { step: 2, title: "Mapping Audience Psychology", subtitle: "Interpreting audience mindset and motivations" },
  { step: 3, title: "Aligning Narrative Angles", subtitle: "Evaluating narrative leverage" },
  { step: 4, title: "Constructing Creative Strategist", subtitle: "Selecting optimal creative strategist mindset" },
  { step: 5, title: "Finalizing Creative Strategist Persona", subtitle: "Preparing strategist profile" },
];

export function CreativeStrategistLoading() {
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    [1, 2, 3, 4].forEach((step) => {
      timers.push(
        setTimeout(() => setCurrentStep(step + 1), step * 4000)
      );
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  const meta = STEPS[currentStep - 1];

  return (
    <div
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0c1e3e 0%, #1a3a6e 50%, #0e2654 100%)",
        backgroundSize: "250% 250%",
        animation: "gradient-shift 10s ease infinite",
      }}
    >
      {/* Dot grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
        }}
      />
      {/* Scan shimmer */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 bottom-0 w-10"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.04), transparent)",
            animation: "scan-shimmer 6s ease-in-out infinite",
            animationDelay: "1s",
          }}
        />
      </div>

      <div className="relative px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
          >
            <StepHeader step={meta.step} title={meta.title} subtitle={meta.subtitle} />
            {currentStep === 1 && <Step1 />}
            {currentStep === 2 && <Step2 />}
            {currentStep === 3 && <Step3 />}
            {currentStep === 4 && <Step4 />}
            {currentStep === 5 && <Step5 />}
          </motion.div>
        </AnimatePresence>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {STEPS.map((s) => (
            <div
              key={s.step}
              className={`rounded-full transition-all duration-500 ${
                s.step === currentStep
                  ? "w-5 h-1.5 bg-cyan-400"
                  : s.step < currentStep
                  ? "w-1.5 h-1.5 bg-cyan-400/60"
                  : "w-1.5 h-1.5 bg-white/15"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
